import { zodResolver } from '@hookform/resolvers/zod';
import { JSX, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { FileMimeTypes, FilePicker, ItemStatus } from '@/modules/common';

import { maxFilePreviewSize } from '../constants';
import { MaskFormSchema, MaskFormValues } from '../validations';

interface MaskFormProps {
  editMode?: boolean;
  defaultValues?: MaskFormValues;
  onSubmit: (data: MaskFormValues) => Promise<void>;
  children: JSX.Element;
}

const defaultFormValues: MaskFormValues = {
  name: '',
  maskUrl: '',
  updatedAt: undefined,
};

export function MaskForm({
  onSubmit,
  children,
  editMode,
  defaultValues,
}: MaskFormProps) {
  const form = useForm<MaskFormValues>({
    resolver: zodResolver(MaskFormSchema),
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues);
    }
  }, [defaultValues]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {children}
        <div className="flex flex-col gap-10 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:grid-cols-2 grid-flow-col grid-rows-6 sm:grid-rows-1">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mask name</FormLabel>
                  <FormControl>
                    <Input placeholder="Add mask name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="updatedAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last modified</FormLabel>
                  <FormControl>
                    <Input
                      disabled
                      placeholder="YYYY-MM-DD"
                      required={false}
                      readOnly
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <ItemStatus
              name="status"
              isDisabled={!editMode}
              control={form.control}
            />
          </div>

          <div className="flex flex-col gap-4">
            <p className="text-sm text-zinc-400">
              The uploaded image is used to generate masks. White areas of the
              uploaded image are removed (cut off) from the main image, while
              non-white pixels — typically black — are preserved to define the
              visible shape and determine which parts of the main image remain.
            </p>
            <FilePicker
              needPrefetch
              label="Mask"
              name="maskUrl"
              withFilePreview
              accept={[FileMimeTypes.Png]}
              placeholder="Add mask file"
              tips={
                <p className="text-sm text-zinc-500">
                  The file must be in
                  <span className="text-zinc-100">&nbsp;.png&nbsp;</span>
                  format and maximum size 10 MB.
                </p>
              }
              maxFileSize={maxFilePreviewSize}
            />
          </div>
        </div>
      </form>
    </Form>
  );
}
